import {offlineDb} from './db';
import {discardOfflineSession} from './offlineSessionManager';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** True when the session has outbox items that still need syncing. */
export const hasOutstandingItems = async (
  sessionId: string
): Promise<boolean> => {
  const count = await offlineDb.outbox
    .where('sessionId')
    .equals(sessionId)
    .filter(
      item =>
        item.status === 'pending' ||
        item.status === 'failed' ||
        item.status === 'syncing'
    )
    .count();
  return count > 0;
};

/**
 * Logout hygiene: discard every session (any user) that has nothing left to
 * sync; retain any with outstanding work so a later login can drain it.
 */
export const purgeAtLogout = async (): Promise<void> => {
  const sessions = await offlineDb.sessions.toArray();
  for (const session of sessions) {
    if (await hasOutstandingItems(session.id)) {
      continue;
    }
    try {
      await discardOfflineSession(session.id);
    } catch (error) {
      console.warn('Logout purge could not discard a session.', error);
    }
  }
};

/**
 * Boot hygiene: discard closed sessions and idle sessions whose cache is older
 * than `maxAgeDays`. Queued work is never age-expired — only a user discards it.
 */
export const runBootJanitor = async (maxAgeDays = 30): Promise<void> => {
  const cutoff = Date.now() - maxAgeDays * MS_PER_DAY;
  const sessions = await offlineDb.sessions.toArray();
  for (const session of sessions) {
    const lastHydratedAt = session.hydratedAt ?? session.startedAt;
    const isExpired = new Date(lastHydratedAt).getTime() < cutoff;
    if (session.status !== 'closed') {
      if (!isExpired) continue;
      if (await hasOutstandingItems(session.id)) continue;
    }
    try {
      await discardOfflineSession(session.id);
    } catch (error) {
      console.warn('Boot janitor could not discard a session.', error);
    }
  }
};
